import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, CircleAlert, Loader2 } from "lucide-react";
import { usePortalAuth } from "@/portal/PortalAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/portal`;
const KEY = "masaya_portal_token";
const inp = "w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] px-3 py-2.5 text-sm text-[#1C1917]";
const btn = "inline-flex items-center justify-center gap-2 rounded-full bg-[#1C1917] text-white px-6 py-3 text-sm font-medium hover:bg-[#292524] disabled:opacity-60";

export default function GuestVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = usePortalAuth();
  const [state, setState] = useState("working");
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setErr("This link is missing its code."); setState("error"); return; }
    axios.post(`${API}/auth/magic/consume`, { token })
      .then(async ({ data }) => {
        localStorage.setItem(KEY, data.token);
        setHasPassword(!!data.has_password);
        await refresh?.();
        setState("ok");
      })
      .catch((e) => { setErr(e.response?.data?.detail || "This sign-in link could not be used."); setState("error"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePw = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await axios.post(`${API}/guest/set-password`, { password: pw }, { headers: { Authorization: `Bearer ${localStorage.getItem(KEY)}` } });
      navigate("/guest/dashboard");
    } catch (er) { setErr(er.response?.data?.detail || "Could not save the password."); } finally { setPwBusy(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16" data-testid="guest-verify-page">
      <div className="w-full max-w-md rounded-2xl border border-[#E7E5E4] bg-[#FAFAF9] p-8 text-center">
        {state === "working" && <><Loader2 className="w-8 h-8 mx-auto animate-spin text-[#8A9A86]" /><p className="mt-4 text-sm text-[#44403C]">Signing you in securely…</p></>}
        {state === "error" && (
          <>
            <CircleAlert className="w-8 h-8 mx-auto text-[#B45309]" />
            <p data-testid="guest-verify-error" className="mt-4 text-sm text-[#44403C]">{err}</p>
            <Link to="/guest" data-testid="guest-verify-request-new" className="mt-6 inline-block text-sm underline underline-offset-4 text-[#1C1917]">Request a new sign-in link</Link>
          </>
        )}
        {state === "ok" && (
          <>
            <CheckCircle2 className="w-8 h-8 mx-auto text-[#4A6A48]" />
            <h1 className="mt-4 text-2xl font-normal text-[#1C1917]">Email verified — you're signed in</h1>
            <p className="mt-2 text-sm text-[#44403C]">You can keep signing in with email links. Optionally, set a password now for faster access.</p>
            {!hasPassword && (
              <form onSubmit={savePw} className="mt-6 space-y-3 text-left" data-testid="guest-set-password-form">
                <label className="text-xs text-[#78716C]">Password (10+ characters, optional)
                  <input type="password" minLength={10} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="guest-set-password-input" className={inp + " mt-1"} autoComplete="new-password" />
                </label>
                {err && <p className="text-sm text-red-600">{err}</p>}
                <button disabled={pwBusy || pw.length < 10} data-testid="guest-set-password-save" className={btn + " w-full"}>{pwBusy ? "Saving…" : "Save password & continue"}</button>
              </form>
            )}
            <button onClick={() => navigate("/guest/dashboard")} data-testid="guest-verify-continue" className="mt-4 text-sm underline underline-offset-4 text-[#1C1917]">
              {hasPassword ? "Continue to my Guest Portal" : "Skip for now — continue to my Guest Portal"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
